import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  useColorScheme,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../types/navigation';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme, ThemeMode } from '../../hooks/useTheme';

interface ThemeOption {
  id: ThemeMode;
  title: string;
  description: string;
  icon: string;
}

const themeOptions: ThemeOption[] = [
  {
    id: 'light',
    title: 'Light',
    description: 'Light background with dark text',
    icon: 'sunny-outline',
  },
  {
    id: 'dark',
    title: 'Dark',
    description: 'Dark background with light text',
    icon: 'moon-outline',
  },
  {
    id: 'system',
    title: 'System',
    description: 'Follow system settings',
    icon: 'settings-outline',
  },
];

const ThemeSelectionScreen = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const insets = useSafeAreaInsets();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for system theme display
  const _deviceTheme = useColorScheme();
  const { theme, setTheme, colors } = useTheme();

  const handleThemeSelect = (themeId: ThemeMode) => {
    setTheme(themeId);

    // Navigate back after selection
    setTimeout(() => {
      navigation.goBack();
    }, 300);
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={{
          paddingBottom: 40 + insets.bottom,
          paddingHorizontal: 20,
        }}
        showsVerticalScrollIndicator={false}
      >
        <View style={[styles.infoSection, { backgroundColor: colors.card }]}>
          <Ionicons
            name="information-circle-outline"
            size={24}
            color={colors.secondary}
          />
          <Text style={[styles.infoText, { color: colors.secondary }]}>
            Choose your preferred theme mode. System mode will automatically
            switch between light and dark based on your device settings.
          </Text>
        </View>

        {themeOptions.map((option) => {
          const isSelected = theme === option.id;

          return (
            <TouchableOpacity
              key={option.id}
              style={[
                styles.themeItem,
                {
                  backgroundColor: colors.card,
                  borderColor: isSelected ? colors.primary : colors.border,
                },
                isSelected && { backgroundColor: colors.border },
              ]}
              onPress={() => handleThemeSelect(option.id)}
            >
              <View style={styles.themeContent}>
                <View
                  style={[
                    styles.themeIconContainer,
                    { backgroundColor: colors.background },
                  ]}
                >
                  <Ionicons
                    name={option.icon as keyof typeof Ionicons.glyphMap}
                    size={24}
                    color={isSelected ? colors.primary : colors.secondary}
                  />
                </View>
                <View style={styles.themeTextContainer}>
                  <Text
                    style={[
                      styles.themeTitle,
                      { color: colors.text },
                      isSelected && { color: colors.primary },
                    ]}
                  >
                    {option.title}
                  </Text>
                  <Text
                    style={[
                      styles.themeDescription,
                      { color: colors.secondary },
                    ]}
                  >
                    {option.description}
                  </Text>
                  {option.id === 'system' && (
                    <Text
                      style={[
                        styles.themeCurrentSystem,
                        { color: colors.primary },
                      ]}
                    >
                      {/* Currently using: {deviceTheme === 'dark' ? 'Dark' : 'Light'} mode */}
                      Currently using: Light mode
                    </Text>
                  )}
                </View>
              </View>
              {isSelected && (
                <Ionicons
                  name="checkmark-circle"
                  size={24}
                  color={colors.primary}
                />
              )}
            </TouchableOpacity>
          );
        })}

        <View style={styles.previewSection}>
          <Text style={[styles.previewTitle, { color: colors.text }]}>
            Preview
          </Text>

          <View style={styles.previewContainer}>
            <View
              style={[
                styles.previewCard,
                styles.lightPreview,
                {
                  backgroundColor: colors.background,
                  borderColor: colors.border,
                },
              ]}
            >
              <Text style={styles.previewCardTitle}>Light</Text>
              <View style={styles.previewContent}>
                <View style={styles.previewElement} />
                <View style={styles.previewElement} />
              </View>
            </View>

            <View
              style={[
                styles.previewCard,
                { backgroundColor: colors.background },
              ]}
            >
              <Text style={[styles.previewCardTitle, { color: colors.text }]}>
                Dark
              </Text>
              <View style={styles.previewContent}>
                <View
                  style={[
                    styles.previewElement,
                    { backgroundColor: colors.border },
                  ]}
                />
                <View
                  style={[
                    styles.previewElement,
                    { backgroundColor: colors.border },
                  ]}
                />
              </View>
            </View>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 60,
    paddingBottom: 16,
    borderBottomWidth: 1,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  scrollView: {
    flex: 1,
  },
  infoSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 20,
    marginVertical: 20,
    borderRadius: 12,
  },
  infoText: {
    fontSize: 14,
    marginLeft: 12,
    flex: 1,
  },
  themeItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
  },
  themeContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  themeIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  themeTextContainer: {
    flex: 1,
  },
  themeTitle: {
    fontSize: 16,
    fontWeight: '500',
  },
  themeDescription: {
    fontSize: 14,
    marginTop: 2,
  },
  themeCurrentSystem: {
    fontSize: 12,
    marginTop: 4,
  },
  previewSection: {
    marginTop: 20,
    marginBottom: 20,
  },
  previewTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  previewContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  previewCard: {
    width: '48%',
    borderRadius: 12,
    padding: 16,
    height: 160,
  },
  lightPreview: {
    borderWidth: 1,
  },
  previewCardTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 12,
  },
  previewContent: {
    flex: 1,
    justifyContent: 'space-around',
  },
  previewElement: {
    height: 24,
    borderRadius: 4,
  },
});

export default ThemeSelectionScreen;
